import { z } from 'zod';

import { LocalDateTime } from '@/shared/modules/date';
import { Resource } from '@/shared/modules/resource/model';
import aixProvenceStationImg from '@/static/images/sites/aix-provence-station.webp';
import bdxMerignacImg from '@/static/images/sites/bdx-merignac.webp';
import bdxStJeanImg from '@/static/images/sites/bdx-stJean.webp';
import beauvaisImg from '@/static/images/sites/beauvais-airport.webp';
import bslAirportImg from '@/static/images/sites/bsl-airport.webp';
import lyonStationImg from '@/static/images/sites/lyon-stEx-station.webp';
import lyonAirportImg from '@/static/images/sites/lyon-stEx.webp';
import mrsProvenceImg from '@/static/images/sites/mrs-provence.webp';
import nantesImg from '@/static/images/sites/nantes.webp';
import niceImg from '@/static/images/sites/nice.webp';
import parisCdgImg from '@/static/images/sites/paris-cdg.webp';
import parisOrlyImg from '@/static/images/sites/paris-orly.webp';
import tlsBlagnacImg from '@/static/images/sites/toulouse-blagnac.webp';
import { OrderedArray } from '@/utils/effect';

export const BVPrice = z.number().brand<'BVPrice'>();
export type BVPrice = z.infer<typeof BVPrice>;

export const SiteId = z.number().brand<'SiteId'>();
export type SiteId = z.infer<typeof SiteId>;

export type SiteCheckpointId = string | number;

export const ServiceCategoryId = z.number().brand<'ServiceCategoryId'>();
export type ServiceCategoryId = z.infer<typeof ServiceCategoryId>;

export namespace Site {
  export enum Key {
    AIX = 'AIX',
    BOD = 'BOD',
    BODG = 'BODG',
    BSL = 'BSL',
    BVA = 'BVA',
    CDG = 'CDG',
    ORY = 'ORY',
    MRS = 'MRS',
    NCE = 'NCE',
    NTE = 'NTE',
    LYS = 'LYS',
    LYSG = 'LYSG',
    TLS = 'TLS',
  }

  export enum Kind {
    Airport = 'airport',
    Station = 'station',
  }

  export const kindSuffix: Record<Kind, string> = {
    [Kind.Airport]: 'flight',
    [Kind.Station]: 'train',
  };

  export const label: Record<Key, string> = {
    [Key.AIX]: 'Aix-en-Provence',
    [Key.BOD]: 'Bordeaux Mérignac',
    [Key.BODG]: 'Bordeaux Saint-Jean',
    [Key.BSL]: 'Bâle-Mulhouse',
    [Key.BVA]: 'Paris Beauvais',
    [Key.CDG]: 'Paris Charles de Gaulle',
    [Key.ORY]: 'Paris Orly',
    [Key.MRS]: 'Marseille Provence',
    [Key.NCE]: 'Nice',
    [Key.NTE]: 'Nantes',
    [Key.LYS]: 'Lyon Saint Exupéry',
    [Key.LYSG]: 'Lyon Saint Exupéry',
    [Key.TLS]: 'Toulouse Blagnac',
  };

  export const code: Record<Key, string> = {
    [Key.AIX]: 'aix-station',
    [Key.BOD]: 'bordeaux-airport',
    [Key.BODG]: 'bordeaux-station',
    [Key.BSL]: 'bale-mulhouse-station',
    [Key.BVA]: 'beauvais-airport',
    [Key.CDG]: 'roissy-airport',
    [Key.ORY]: 'orly-airport',
    [Key.MRS]: 'marseille-airport',
    [Key.NCE]: 'nice-airport',
    [Key.NTE]: 'nantes-airport',
    [Key.LYS]: 'lyon-airport',
    [Key.LYSG]: 'lyon-station',
    [Key.TLS]: 'toulouse-airport',
  };

  export const kind: Record<Key, Kind> = {
    [Key.AIX]: Kind.Station,
    [Key.BOD]: Kind.Airport,
    [Key.BODG]: Kind.Station,
    [Key.BSL]: Kind.Airport,
    [Key.BVA]: Kind.Airport,
    [Key.CDG]: Kind.Airport,
    [Key.ORY]: Kind.Airport,
    [Key.MRS]: Kind.Airport,
    [Key.NCE]: Kind.Airport,
    [Key.NTE]: Kind.Airport,
    [Key.LYS]: Kind.Airport,
    [Key.LYSG]: Kind.Station,
    [Key.TLS]: Kind.Airport,
  };

  export const image: Record<Key, string> = {
    [Key.AIX]: aixProvenceStationImg,
    [Key.BOD]: bdxMerignacImg,
    [Key.BODG]: bdxStJeanImg,
    [Key.BSL]: bslAirportImg,
    [Key.BVA]: beauvaisImg,
    [Key.CDG]: parisCdgImg,
    [Key.ORY]: parisOrlyImg,
    [Key.MRS]: mrsProvenceImg,
    [Key.NCE]: niceImg,
    [Key.NTE]: nantesImg,
    [Key.LYS]: lyonAirportImg,
    [Key.LYSG]: lyonStationImg,
    [Key.TLS]: tlsBlagnacImg,
  };

  export const keys = OrderedArray.fromKeys(
    Site.Key,
    Site.Key.BOD,
    Site.Key.BODG,
    Site.Key.BSL,
    Site.Key.ORY,
    Site.Key.CDG,
    Site.Key.LYS,
    Site.Key.LYSG,
    Site.Key.TLS,
    Site.Key.MRS,
    Site.Key.AIX,
    Site.Key.NCE,
    Site.Key.NTE,
    Site.Key.BVA,
  );
}

export namespace Api {
  interface Checkpoint {
    id: SiteCheckpointId;
    name: string;
  }

  export interface Site {
    id: SiteId;
    name: string;
    maxHeight: number | null;
    checkpoints: Array<Checkpoint>;
    type: Site.Kind;
    airportCode: string;
  }

  export interface SiteSwitch {
    from: SiteId;
    to: SiteId;
    serviceSiteId: SiteId;
  }

  export interface Referential {
    sites: Array<Site>;
    switches: Array<SiteSwitch>;
  }

  export interface SiteSlots {
    step: number;
    startTime: string;
    endTime: string;
    unavailables: Array<number>;
  }

  export interface Service {
    id: number;
    label: string;
    description: string;
    price: BVPrice;
    enabledByDefault: boolean;
    disabledForShortBooking: boolean;
    disabledForWeekEnd: boolean;
    isDisabled: boolean;
    image: Resource | null;
    isExclusiveWith: ServiceCategoryId | null;
    isInsurance: boolean;
  }

  export interface FastBookingEstimation {
    total: BVPrice;
    totalWithoutPromotion: BVPrice | null;
    priceWithoutPromotion: BVPrice | null;
    durationInDays: number;
    timeSaved: number;
    switchPrice: BVPrice | null;
    localPrice: BVPrice | null;
    priceSaved: number | null;
  }
}

export namespace BVScheduledFlight {
  export const Number = z.string().brand<'BVScheduledFlightNumber'>();
  export type Number = z.infer<typeof Number>;

  export interface Airline {
    code: string;
    name: string;
  }

  export interface Airport {
    code: string;
    name: string;
    city: string;
  }
}

export namespace BVScheduledTrain {
  export const Number = z.string().brand<'BVScheduledTrainNumber'>();
  export type Number = z.infer<typeof Number>;
}

export namespace BVScheduledTransport {
  export enum Type {
    Flight = 'FLIGHT',
    Train = 'TRAIN',
  }
}

export interface BVScheduledFlight {
  type: BVScheduledTransport.Type.Flight;
  carrier: BVScheduledFlight.Airline;
  number: BVScheduledFlight.Number;
  departureAirport: BVScheduledFlight.Airport;
  departureTerminal: string | null;
  departureTime: LocalDateTime;
  departureCheckpoint: SiteCheckpointId | null;
  arrivalAirport: BVScheduledFlight.Airport;
  arrivalTerminal: string | null;
  arrivalTime: LocalDateTime;
  arrivalCheckpoint: SiteCheckpointId | null;
}

interface BVScheduledTrain {
  type: BVScheduledTransport.Type.Train;
  number: BVScheduledTrain.Number;
  departureStation: string | null;
  departureTime: LocalDateTime;
  arrivalStation: string | null;
  arrivalTime: LocalDateTime;
}

export type BVScheduledTransport = BVScheduledFlight | BVScheduledTrain;
